"use client";

import { c as _c } from "react/compiler-runtime";
import { ParentSize } from "@visx/responsive";
import { scaleLinear, scaleTime } from "@visx/scale";
import { bisector } from "d3-array";
import { Children, isValidElement, type ReactElement, type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { cn } from "@/lib/utils";
import { Area, type AreaProps } from "./area";
import { ChartProvider, type LineConfig, type Margin } from "./chart-context";
import { useChartInteraction } from "./use-chart-interaction";

// Check if a component should render after the mouse overlay (markers need to be on top for interaction)
function isPostOverlayComponent(child: ReactElement): boolean {
  const childType = child.type as {
    displayName?: string;
    name?: string;
    __isChartMarkers?: boolean;
  };

  // Check for static marker property (more reliable than displayName)
  if (childType.__isChartMarkers) {
    return true;
  }

  // Fallback to displayName check
  const componentName = typeof child.type === "function" ? childType.displayName || childType.name || "" : "";
  return componentName === "ChartMarkers" || componentName === "MarkerGroup";
}
export interface AreaChartProps {
  /** Data array - each item should have a date field and numeric values */
  data: Record<string, unknown>[];
  /** Key in data for the x-axis (date). Default: "date" */
  xDataKey?: string;
  /** Chart margins */
  margin?: Partial<Margin>;
  /** Animation duration in milliseconds. Default: 1100 */
  animationDuration?: number;
  /** Aspect ratio as "width / height". Default: "2 / 1" */
  aspectRatio?: string;
  /** Additional class name for the container */
  className?: string;
  /** Child components (Area, Grid, ChartTooltip, etc.) */
  children: ReactNode;
}
const DEFAULT_MARGIN: Margin = {
  top: 40,
  right: 40,
  bottom: 40,
  left: 40
};

// Extract area/line configs from children synchronously to avoid render timing issues
function extractAreaConfigs(children: ReactNode): LineConfig[] {
  const configs: LineConfig[] = [];
  Children.forEach(children, child => {
    if (!isValidElement(child)) {
      return;
    }

    // Check if it's an Area component by displayName, function reference, or props structure
    const childType = child.type as {
      displayName?: string;
      name?: string;
    };
    const componentName = typeof child.type === "function" ? childType.displayName || childType.name || "" : "";

    // Check by displayName, or by props having dataKey (duck typing)
    const props = child.props as AreaProps | undefined;
    const isAreaComponent = componentName === "Area" || child.type === Area || props && typeof props.dataKey === "string" && props.dataKey.length > 0;
    if (isAreaComponent && props?.dataKey) {
      configs.push({
        dataKey: props.dataKey,
        stroke: props.stroke || props.fill || "var(--chart-line-primary)",
        strokeWidth: props.strokeWidth || 2
      });
    }
  });
  return configs;
}
interface ChartInnerProps {
  width: number;
  height: number;
  data: Record<string, unknown>[];
  xDataKey: string;
  margin: Margin;
  animationDuration: number;
  children: ReactNode;
  containerRef: React.RefObject<HTMLDivElement | null>;
}
function ChartInner({
  width,
  height,
  data,
  xDataKey,
  margin,
  animationDuration,
  children,
  containerRef
}: ChartInnerProps) {
  const [isLoaded, setIsLoaded] = useState(false);

  // Extract area configs synchronously from children
  const lines = useMemo(() => extractAreaConfigs(children), [children]);
  const innerWidth = width - margin.left - margin.right;
  const innerHeight = height - margin.top - margin.bottom;

  // X accessor function
  const xAccessor = useCallback((d: Record<string, unknown>): Date => {
    const value = d[xDataKey];
    return value instanceof Date ? value : new Date(value as string | number);
  }, [xDataKey]);

  // Create bisector for finding nearest data point
  const bisectDate = useMemo(() => bisector<Record<string, unknown>, Date>(d_0 => xAccessor(d_0)).left, [xAccessor]);

  // X scale (time) - use exact data domain for tight fit
  const xScale = useMemo(() => {
    const dates = data.map(d_1 => xAccessor(d_1));
    const minTime = Math.min(...dates.map(d_2 => d_2.getTime()));
    const maxTime = Math.max(...dates.map(d_3 => d_3.getTime()));
    return scaleTime({
      range: [0, innerWidth],
      domain: [minTime, maxTime]
    });
  }, [innerWidth, data, xAccessor]);

  // Calculate column width (spacing between data points)
  const columnWidth = useMemo(() => {
    if (data.length < 2) {
      return 0;
    }
    return innerWidth / (data.length - 1);
  }, [innerWidth, data.length]);

  // Y scale - computed from extracted area configs (available immediately)
  const yScale = useMemo(() => {
    let maxValue = 0;
    for (const line of lines) {
      for (const d_4 of data) {
        const value_0 = d_4[line.dataKey];
        if (typeof value_0 === "number" && value_0 > maxValue) {
          maxValue = value_0;
        }
      }
    }
    if (maxValue === 0) {
      maxValue = 100;
    }
    return scaleLinear({
      range: [innerHeight, 0],
      domain: [0, maxValue * 1.1],
      nice: true
    });
  }, [innerHeight, data, lines]);

  // Pre-compute date labels for ticker animation
  const dateLabels = useMemo(() => data.map(d_5 => {
    const date = xAccessor(d_5);
    const fmt: Intl.DateTimeFormatOptions = {
      month: "short",
      day: "numeric"
    };
    // Include hour if multiple points share the same day
    if (data.length > 1) {
      const first = xAccessor(data[0]);
      const last = xAccessor(data[data.length - 1]);
      const spanDays = (last.getTime() - first.getTime()) / (1000 * 60 * 60 * 24);
      if (spanDays < data.length * 0.8) {
        fmt.hour = "numeric";
      }
    }
    return date.toLocaleDateString("en-US", fmt);
  }), [data, xAccessor]);

  // Animation timing
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoaded(true);
    }, animationDuration);
    return () => clearTimeout(timer);
  }, [animationDuration]);
  const canInteract = isLoaded;
  const {
    tooltipData,
    setTooltipData,
    selection,
    clearSelection,
    interactionHandlers,
    interactionStyle
  } = useChartInteraction({
    xScale,
    yScale,
    data,
    lines,
    margin,
    xAccessor,
    bisectDate,
    canInteract
  });

  // Early return if dimensions not ready
  if (width < 10 || height < 10) {
    return null;
  }

  // Separate children into pre-overlay (Grid, Area) and post-overlay (ChartMarkers)
  const preOverlayChildren: ReactElement[] = [];
  const postOverlayChildren: ReactElement[] = [];
  Children.forEach(children, child => {
    if (!isValidElement(child)) {
      return;
    }
    if (isPostOverlayComponent(child)) {
      postOverlayChildren.push(child);
    } else {
      preOverlayChildren.push(child);
    }
  });
  const contextValue = {
    data,
    xScale,
    yScale,
    width,
    height,
    innerWidth,
    innerHeight,
    margin,
    columnWidth,
    tooltipData,
    setTooltipData,
    containerRef,
    lines,
    isLoaded,
    animationDuration,
    xAccessor,
    dateLabels,
    selection,
    clearSelection
  };
  return <ChartProvider value={contextValue}>
      <svg aria-hidden="true" height={height} width={width}>
        <defs>
          <clipPath id="chart-area-grow-clip">
            <rect height={innerHeight + 20} style={{
            transition: isLoaded ? "none" : `width ${animationDuration}ms cubic-bezier(0.85, 0, 0.15, 1)`
          }} width={isLoaded ? innerWidth : 0} x={0} y={0} />
          </clipPath>
        </defs>

        <rect fill="transparent" height={height} width={width} x={0} y={0} />

        <g {...interactionHandlers} style={interactionStyle} transform={`translate(${margin.left},${margin.top})`}>
          <rect fill="transparent" height={innerHeight} width={innerWidth} x={0} y={0} />

          {preOverlayChildren}
          {postOverlayChildren}
        </g>
      </svg>
    </ChartProvider>;
}
export function AreaChart(t0) {
  const $ = _c(16);
  const {
    data,
    xDataKey: t1,
    margin: marginProp,
    animationDuration: t2,
    aspectRatio: t3,
    className: t4,
    children
  } = t0;
  const xDataKey = t1 === undefined ? "date" : t1;
  const animationDuration = t2 === undefined ? 1100 : t2;
  const aspectRatio = t3 === undefined ? "2 / 1" : t3;
  const className = t4 === undefined ? "" : t4;
  const containerRef = useRef(null);
  let t5;
  if ($[0] !== marginProp) {
    t5 = {
      ...DEFAULT_MARGIN,
      ...marginProp
    };
    $[0] = marginProp;
    $[1] = t5;
  } else {
    t5 = $[1];
  }
  const margin = t5;
  let t6;
  if ($[2] !== className) {
    t6 = cn("relative w-full", className);
    $[2] = className;
    $[3] = t6;
  } else {
    t6 = $[3];
  }
  let t7;
  if ($[4] !== aspectRatio) {
    t7 = {
      aspectRatio,
      touchAction: "none"
    };
    $[4] = aspectRatio;
    $[5] = t7;
  } else {
    t7 = $[5];
  }
  let t8;
  if ($[6] !== animationDuration || $[7] !== children || $[8] !== data || $[9] !== margin || $[10] !== xDataKey) {
    t8 = <ParentSize debounceTime={10}>{t9 => {
        const {
          width,
          height
        } = t9;
        return <ChartInner animationDuration={animationDuration} containerRef={containerRef} data={data} height={height} margin={margin} width={width} xDataKey={xDataKey}>{children}</ChartInner>;
      }}</ParentSize>;
    $[6] = animationDuration;
    $[7] = children;
    $[8] = data;
    $[9] = margin;
    $[10] = xDataKey;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  let t9;
  if ($[12] !== t6 || $[13] !== t7 || $[14] !== t8) {
    t9 = <div className={t6} ref={containerRef} style={t7}>{t8}</div>;
    $[12] = t6;
    $[13] = t7;
    $[14] = t8;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  return t9;
}

// Re-export Area for convenience
export { Area, type AreaProps } from "./area";
export default AreaChart;